import { Arry } from './Arry.js';
import { Rndm } from './Rndm.js';
import { V2 } from './V2.js';
import { loop_type } from './Loop.js';

interface EaseItem {
    id: string;
    now: number;
    t: number;
    ease: string;
    timeStart: number | null;
    timeEnd: number | null;
    duration: number | null;
    delay: number | null;
    end?: (() => void) | null;
    init?: () => void;
    once?: boolean;
    what?: (value: number) => void;
    start?: number;
    aim?: number;
    endless?: boolean;
    kill(): void;
    [key: string]: any;
}

interface DynamicOptions {
    now: { x: number; y: number };
    aim: { x: number; y: number };
    speed: number;
}

interface EaseMakeOptions {
    id?: string;
    start?: number;
    aim?: number;
    duration?: number; //ms
    delay?: number; //ms
    timeStart?: number;
    timeEnd?: number;
    ease?: string;
    endless?: boolean;
    what?: (value: number) => void;
    init?: () => void;
    end?: (() => void) | null;
    [key: string]: any;
}

interface EaseOptions {
    auto_add?: boolean;
}

const presets: Required<EaseOptions> = {
    auto_add: true,
};

export const Ease = {
    list: [] as EaseItem[],
    add: function (io: EaseItem): void {
        this.list.push(io);
    },
    get: function (id: string): EaseItem | false {
        const result = Arry.get.id(this.list, id);
        if (result && typeof result === 'object' && 'now' in result) {
            return result as EaseItem;
        }
        return false;
    },
    kill: function (id: string): void {
        const item = this.get(id);
        if (item && item.kill) item.kill();
    },
    make: function (io: EaseMakeOptions = {}, o?: EaseOptions): EaseItem {

        const options = { ...presets, ...o };

        if (io.id) Ease.kill(io.id); //retrigger: replace a running ease with the same id

        const t: EaseItem = {
            now: io.start ?? 0,
            t: 0,
            ease: io.ease || "linear",
            timeStart: null,
            timeEnd: null,
            duration: null,
            delay: null,
            end: io.end || null,
            ...io,
            id: io.id || Rndm.id(),
            kill: function (): void {
                Arry.remove.id(Ease.list, this.id);
            },
        };

        t.timeStart = io.timeStart ?? performance.now();
        if (!io.duration && io.timeEnd) t.duration = io.timeEnd - t.timeStart;
        if (io.delay) t.timeStart += io.delay;
        t.timeEnd = t.timeStart + (t.duration || 0);

        if (options.auto_add) Ease.add(t);
        return t;
    },

    run: function (time?: loop_type): void {
        const now = time ? time.now : performance.now();
        for (let i = this.list.length - 1; i >= 0; i--) {
            const e = this.list[i];
            if (e.timeStart === null || e.timeStart > now) continue;
            if (e.init && !e.once) {
                e.init();
                e.once = true;
            }
            e.t = (e.duration && e.duration > 0) ? Math.min((now - e.timeStart) / e.duration, 1) : 1;
            if (e.what && e.start !== undefined && e.aim !== undefined) {
                const easingFunc = Ease[e.ease as keyof typeof Ease];
                if (typeof easingFunc === 'function') {
                    // Handle functions that take 1 or 2 parameters
                    const easedValue = easingFunc.length === 2 ? (easingFunc as (t: number, pow?: number) => number)(e.t, 2) : (easingFunc as (t: number) => number)(e.t);
                    e.now = e.start + easedValue * (e.aim - e.start);
                    e.what(e.now);
                }
            }
            if (e.t >= 1 && !e.endless) {
                if (e.end) e.end();
                e.kill();
            }
        }
    },

    //start with 0, end with 1

    //by https://gist.github.com/gre
    linear: function (t: number): number { return t; },
    quadIn: function (t: number): number { return t * t; },
    quadOut: function (t: number): number { return t * (2 - t); },
    quadInOut: function (t: number): number { return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; },
    cubicIn: function (t: number): number { return t * t * t; },
    cubicOut: function (t: number): number { 
        const t1 = t - 1;
        return t1 * t1 * t1 + 1; 
    },
    cubicInOut: function (t: number): number { return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1; },
    cubicOutIn: function (t: number): number { return t < 0.5 ? 2 * t - 2 * t * t * t : 2 * (1 - t) + 2 * (t - 1) * (t - 1) * (t - 1); },
    quartIn: function (t: number): number { return t * t * t * t; },
    quartOut: function (t: number): number { 
        const t1 = t - 1;
        return 1 - t1 * t1 * t1 * t1; 
    },
    quartInOut: function (t: number): number { 
        if (t < 0.5) return 8 * t * t * t * t;
        const t1 = t - 1;
        return 1 - 8 * t1 * t1 * t1 * t1; 
    },
    quintIn: function (t: number): number { return t * t * t * t * t; },
    quintOut: function (t: number): number { 
        const t1 = t - 1;
        return 1 + t1 * t1 * t1 * t1 * t1; 
    },
    quintInOut: function (t: number): number { 
        if (t < 0.5) return 16 * t * t * t * t * t;
        const t1 = t - 1;
        return 1 + 16 * t1 * t1 * t1 * t1 * t1; 
    },
    sixtIn: function (t: number): number { return t * t * t * t * t * t; },
    sixtOut: function (t: number): number { 
        const t1 = t - 1;
        return 1 - t1 * t1 * t1 * t1 * t1 * t1; 
    },
    sixtInOut: function (t: number): number { 
        if (t < 0.5) return 32 * t * t * t * t * t * t;
        const t1 = t - 1;
        return 1 + 32 * t1 * t1 * t1 * t1 * t1 * t1; 
    },
    octIn: function (t: number): number { return t * t * t * t * t * t * t * t; },
    octOut: function (t: number): number { 
        const t1 = t - 1;
        return 1 - t1 * t1 * t1 * t1 * t1 * t1 * t1 * t1; 
    },
    dixIn: function (t: number): number { return t * t * t * t * t * t * t * t * t * t; },

    wave: function (t: number): number { return Math.sin(Math.PI * t); }, //0 = 0, 0.5 = 1, 1 = 0
    waveReversed: function (t: number): number { return Math.cos(Math.PI * t); },

    powIn: function (t: number, pow: number): number { return Math.pow(t, pow); },
    powOut: function (t: number, pow: number): number { return 1 - Math.pow(1 - t, pow); },
    powInOut: function (t: number, pow: number): number { 
        const t2 = t * 2;
        return t2 < 1 ? 0.5 * Math.pow(t2, pow) : 1 - 0.5 * Math.abs(Math.pow(2 - t2, pow)); 
    },

    //by https://github.com/pixelass
    sineIn: function (t: number): number { return -1 * Math.cos(t * (Math.PI / 2)) + 1; },
    sineOut: function (t: number): number { return Math.sin(t * (Math.PI / 2)); },
    sineInOut: function (t: number): number { return 0.5 - 0.5 * Math.cos(Math.PI * t); },

    elasticIn: function (t: number, magnitude: number = 0.7): number {
        if (t === 0 || t === 1) {
            return t;
        }
        const scaledTime = t / 1;
        const scaledTime1 = scaledTime - 1;
        const p = 1 - magnitude;
        const s = p / (2 * Math.PI) * Math.asin(1);
        return -(
            Math.pow(2, 10 * scaledTime1) *
            Math.sin((scaledTime1 - s) * (2 * Math.PI) / p)
        );
    },
    elasticOut: function (t: number, magnitude: number = 0.7): number {
        const p = 1 - magnitude;
        const scaledTime = t * 2;
        if (t === 0 || t === 1) {
            return t;
        }
        const s = p / (2 * Math.PI) * Math.asin(1);
        return (
            Math.pow(2, -10 * scaledTime) *
            Math.sin((scaledTime - s) * (2 * Math.PI) / p)
        ) + 1;
    },
    bounceOut: function (t: number): number {
        const scaledTime = t / 1;
        if (scaledTime < (1 / 2.75)) {
            return 7.5625 * scaledTime * scaledTime;
        } else if (scaledTime < (2 / 2.75)) {
            const scaledTime2 = scaledTime - (1.5 / 2.75);
            return (7.5625 * scaledTime2 * scaledTime2) + 0.75;
        } else if (scaledTime < (2.5 / 2.75)) {
            const scaledTime2 = scaledTime - (2.25 / 2.75);
            return (7.5625 * scaledTime2 * scaledTime2) + 0.9375;
        } else {
            const scaledTime2 = scaledTime - (2.625 / 2.75);
            return (7.5625 * scaledTime2 * scaledTime2) + 0.984375;
        }
    },

    dynamic: function (io: DynamicOptions): { x: number; y: number } {
        const diff = V2.new.sub(io.aim, io.now);
        const scaled = V2.new.scale(diff, io.speed);
        return V2.new.add(io.now, scaled);
    },
    circle: function (t: number): number {
        return Math.sqrt(1 - Math.pow(2 * t - 1, 2));
    }

};
